import { initReactI18next } from 'react-i18next';

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

import { isNotProductionEnvironment } from '@treneo/environments';
import { appDefaultLanguageCode, getSupportedLanguageCodes } from '@treneo/shared/constants';
import { logger } from '@treneo/shared/logger';

const SUPPORTED_LANGUAGES = getSupportedLanguageCodes();
const ALL_SUPPORTED_LANGUAGES = [...SUPPORTED_LANGUAGES, 'en', 'pl'];

const initPromise = i18next
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: appDefaultLanguageCode,
    fallbackLng: appDefaultLanguageCode,
    debug: isNotProductionEnvironment(),
    supportedLngs: ALL_SUPPORTED_LANGUAGES,
    ns: ['translation', 'errors'],
    defaultNS: 'translation',
    react: {
      useSuspense: true,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: false,
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'language',
      caches: ['localStorage'],
      excludeCacheFor: ['cimode']
    },
    backend: {
      loadPath: (lngs: string[], namespaces: string[]) => {
        const lng = lngs[0];
        const ns = namespaces[0];

        const languageMap: Record<string, string> = {
          en: 'en-US',
          pl: 'pl-PL'
        };

        const mappedLng = languageMap[lng] || lng;

        if (ns === 'errors') {
          return `/locales/${mappedLng}/errors/errors.json`;
        }

        return `/locales/${mappedLng}/${ns}.json`;
      },
      requestOptions: {
        cache: 'default',
        credentials: 'same-origin',
        mode: 'cors'
      }
    },
    interpolation: {
      escapeValue: false
    },
    saveMissing: isNotProductionEnvironment(),
    missingKeyHandler: (
      lngs: readonly string[],
      _ns: string,
      key: string,
      _fallbackValue: string,
      _updateMissing: boolean,
      _options: unknown
    ) => {
      if (isNotProductionEnvironment() && typeof key === 'string') {
        const lng = lngs[0];
        logger.warn(`Missing translation key: ${key} for language: ${lng}`);
      }
    }
  });

export const i18nReady = initPromise.then(() => i18next.loadNamespaces(['translation', 'errors']));

export { i18next };
